import { Component, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { BankIdVerificationDeliveryMethod } from '@app/sales/modules/details/models/private-customer.model';
import { CustomerSelectors } from '@app/sales/modules/details/states/customer.selectors';
import {
  ResetBankIdEmailAction,
  ResetBankIdMsisdnAction,
  SetBankIdBypassNote,
  SetBankIdEmailAction,
  SetBankIdMsisdnAction,
  ToggleIsBankIdBypassed,
} from '@app/sales/states/order/order.actions';
import { OrderSelectors } from '@app/sales/states/order/order.selectors';
import { Regex } from '@app/shared/enums/regex';
import { select, Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { IceButtonComponent } from '../atoms/ice-button/ice-button.component';
import { IceAlertComponent } from '../molecules/ice-alert/ice-alert.component';
import { IceInputFieldComponent } from '../molecules/ice-input-field/ice-input-field.component';
import { IceTextareaComponent } from '../molecules/ice-textarea/ice-textarea.component';

export interface BankIdVerificationDeliveryPayload {
  method: BankIdVerificationDeliveryMethod;
  msisdn?: string;
  email?: string;
}

type BankIdVerificationFormGroup = FormGroup<{
  bankIdVerificationDeliveryMethod: FormControl<BankIdVerificationDeliveryMethod>;
  bankIdMsisdn: FormControl<string>;
  bankIdEmail: FormControl<string>;
}>;

@Component({
  selector: 'app-bank-id-input',
  imports: [
    ReactiveFormsModule,
    IceInputFieldComponent,
    FormsModule,
    IceAlertComponent,
    IceButtonComponent,
    MatCheckbox,
    IceTextareaComponent,
    MatButtonToggleGroup,
    MatButtonToggle,
  ],
  templateUrl: './bankid-input.component.html',
  styleUrl: './bankid-input.component.scss',
})
export class BankIdInput implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private store = inject(Store);

  BankIdVerificationDeliveryMethod = BankIdVerificationDeliveryMethod;

  bankIdVerification = select(CustomerSelectors.bankIdVerification);

  initialPhoneNumber = input<string | null>(null);
  initialEmail = input<string | null>(null);
  initialDeliveryMethod = input<BankIdVerificationDeliveryMethod>(BankIdVerificationDeliveryMethod.SMS);
  showButton = input<boolean>(false);
  buttonText = input<string | null>(null);
  autoUpdateStore = input<boolean>(false);
  displayBypassOption = input<boolean>(true);
  shouldUsePadding = input<boolean>(true);

  sendBankIdVerification = output<BankIdVerificationDeliveryPayload>();

  canBypassBankId = select(DealerStateSelectors.canBypassBankId);

  subscriptions$: Subscription[] = [];
  isBypassBankIdToggled = false;

  bankIdVerificationForm!: BankIdVerificationFormGroup;

  bypassBankIdForm = this.formBuilder.group({
    reasonNote: ['', [Validators.required, Validators.maxLength(255)]],
  });

  onSubmit() {
    if (!this.showButton() || !this.bankIdVerificationForm.valid) {
      return;
    }

    const method = this.bankIdVerificationForm.controls.bankIdVerificationDeliveryMethod.value;
    const payload: BankIdVerificationDeliveryPayload =
      method === BankIdVerificationDeliveryMethod.SMS
        ? {
            method,
            msisdn: this.bankIdVerificationForm.controls.bankIdMsisdn.value,
          }
        : {
            method,
            email: this.bankIdVerificationForm.controls.bankIdEmail.value,
          };

    this.sendBankIdVerification.emit(payload);

    if (method === BankIdVerificationDeliveryMethod.SMS) {
      const { bankIdMsisdn } = this.bankIdVerificationForm.controls;
      bankIdMsisdn.reset('', { emitEvent: false });
      bankIdMsisdn.markAsPristine();
      bankIdMsisdn.markAsUntouched();
    } else {
      const { bankIdEmail } = this.bankIdVerificationForm.controls;
      bankIdEmail.reset('', { emitEvent: false });
      bankIdEmail.markAsPristine();
      bankIdEmail.markAsUntouched();
    }
  }

  ngOnInit(): void {
    this.initializeBankIdVerificationForm();
    this.bypassBankIdForm.disable();

    const msisdnControl = this.bankIdVerificationForm.controls.bankIdMsisdn;
    const emailControl = this.bankIdVerificationForm.controls.bankIdEmail;
    const methodControl = this.bankIdVerificationForm.controls.bankIdVerificationDeliveryMethod;

    this.subscriptions$.push(
      methodControl.valueChanges.subscribe(method => {
        this.toggleDeliveryControls(method);
      })
    );

    this.subscriptions$.push(
      msisdnControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(value => {
        if (!this.bankIdVerificationForm.enabled || methodControl.value !== BankIdVerificationDeliveryMethod.SMS) {
          return;
        }

        const msisdn = value || '';

        if (this.autoUpdateStore()) {
          this.store.dispatch(new SetBankIdMsisdnAction(msisdn));
        }
      })
    );

    this.subscriptions$.push(
      emailControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(value => {
        if (!this.bankIdVerificationForm.enabled || methodControl.value !== BankIdVerificationDeliveryMethod.Email) {
          return;
        }

        const email = value || '';
        if (this.autoUpdateStore()) {
          this.store.dispatch(new SetBankIdEmailAction(email));
        }
      })
    );

    this.subscriptions$.push(
      this.bypassBankIdForm.controls.reasonNote.valueChanges.subscribe(value => {
        const note = value || '';
        this.store.dispatch(new SetBankIdBypassNote(note));
      })
    );
  }

  private initializeBankIdVerificationForm() {
    const method = this.initialDeliveryMethod() ?? BankIdVerificationDeliveryMethod.SMS;
    const msisdn = this.initialPhoneNumber() ?? this.store.selectSnapshot(OrderSelectors.bankIdMsisdn) ?? '';
    const email = this.initialEmail() ?? this.store.selectSnapshot(OrderSelectors.bankIdEmail) ?? '';
    const isSms = method === BankIdVerificationDeliveryMethod.SMS;

    this.bankIdVerificationForm = this.formBuilder.group({
      bankIdVerificationDeliveryMethod: this.formBuilder.control(method, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      bankIdMsisdn: this.formBuilder.control(
        { value: msisdn, disabled: !isSms },
        {
          nonNullable: true,
          validators: [Validators.required, Validators.pattern(Regex.Phone)],
        }
      ),
      bankIdEmail: this.formBuilder.control(
        { value: email, disabled: isSms },
        {
          nonNullable: true,
          validators: [Validators.required, Validators.pattern(Regex.Email)],
        }
      ),
    }) as BankIdVerificationFormGroup;

    this.toggleDeliveryControls(method);
  }

  toggleBypassBankId({ checked }: MatCheckboxChange) {
    this.isBypassBankIdToggled = checked;
    this.store.dispatch(new ToggleIsBankIdBypassed(checked));

    if (checked) {
      this.bankIdVerificationForm.disable();
      this.bypassBankIdForm.enable();
    } else {
      this.bankIdVerificationForm.enable();
      this.bypassBankIdForm.disable();
    }
  }

  get isCustomerBankIdVerified(): boolean {
    return this.bankIdVerification()?.status === 'Authenticated';
  }

  private toggleDeliveryControls(method: BankIdVerificationDeliveryMethod) {
    const msisdnControl = this.bankIdVerificationForm.controls.bankIdMsisdn;
    const emailControl = this.bankIdVerificationForm.controls.bankIdEmail;

    if (method === BankIdVerificationDeliveryMethod.SMS) {
      msisdnControl.enable({ emitEvent: false });
      msisdnControl.markAsPristine();
      msisdnControl.markAsUntouched();
      emailControl.disable({ emitEvent: false });
      this.store.dispatch(new ResetBankIdEmailAction());
    } else {
      emailControl.enable({ emitEvent: false });
      emailControl.markAsPristine();
      emailControl.markAsUntouched();
      msisdnControl.disable({ emitEvent: false });
      this.store.dispatch(new ResetBankIdMsisdnAction());
    }
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(sub => sub.unsubscribe());
  }

  get bankIdVerificationDeliveryMethod(): string {
    return this.bankIdVerificationForm.controls.bankIdVerificationDeliveryMethod.value ===
      BankIdVerificationDeliveryMethod.SMS
      ? 'mobilnummeret'
      : 'e-posten';
  }

  shouldShowRequiredAlert(): boolean {
    if (!this.bankIdVerificationForm || this.isBypassBankIdToggled) {
      return false;
    }

    const method = this.bankIdVerificationForm.controls.bankIdVerificationDeliveryMethod.value;
    const activeControl =
      method === BankIdVerificationDeliveryMethod.SMS
        ? this.bankIdVerificationForm.controls.bankIdMsisdn
        : this.bankIdVerificationForm.controls.bankIdEmail;

    return activeControl.invalid && (activeControl.dirty || activeControl.touched);
  }
}
